import { DOCUMENT, ViewportScroller } from '@angular/common';
import { inject, Injectable } from '@angular/core';

import { injectWindow } from '@cosmos/util-common/di';

/**
 * Holds the element that actually scrolls.
 *
 * `CustomerPortalProjectsHostComponent` is `height: 100vh; overflow: auto`
 * (its drawer needs a bounded host), so everything routed inside it scrolls
 * that element and the document never moves. It registers itself here so
 * {@link CustomerPortalViewportScroller} knows what to drive.
 */
@Injectable({ providedIn: 'root' })
export class CustomerPortalScrollContainer {
  private _element: HTMLElement | null = null;

  get element(): HTMLElement | null {
    return this._element;
  }

  register(element: HTMLElement): void {
    this._element = element;
  }

  unregister(element: HTMLElement): void {
    if (this._element === element) {
      this._element = null;
    }
  }
}

/**
 * `ViewportScroller` that drives the registered scroll container instead of
 * `window`.
 *
 * Angular's default implementation always scrolls `window`, which in this app
 * never moves — that made `withInMemoryScrolling` and every
 * `scrollToPosition` call silently do nothing. The router stores and restores
 * positions through this same interface, so pointing it at the real container
 * fixes both scroll-to-top and back/forward restoration.
 *
 * Routes rendered outside the host (auth, standalone pages) do scroll the
 * document, so fall back to `window` while nothing is registered.
 */
@Injectable()
export class CustomerPortalViewportScroller implements ViewportScroller {
  private readonly _window = injectWindow();
  private readonly _document = inject(DOCUMENT);
  private readonly _container = inject(CustomerPortalScrollContainer);

  private _offset: () => [number, number] = () => [0, 0];

  setOffset(offset: [number, number] | (() => [number, number])): void {
    this._offset = typeof offset === 'function' ? offset : () => offset;
  }

  getScrollPosition(): [number, number] {
    const element = this._container.element;
    return element
      ? [element.scrollLeft, element.scrollTop]
      : [this._window.scrollX, this._window.scrollY];
  }

  scrollToPosition(position: [number, number], options?: ScrollOptions): void {
    const [left, top] = position;
    this._scrollTarget().scrollTo({ left, top, ...options });
  }

  scrollToAnchor(target: string, options?: ScrollOptions): void {
    const anchor =
      this._document.getElementById(target) ??
      this._document.getElementsByName(target)[0];

    if (!anchor) {
      return;
    }

    anchor.scrollIntoView({ block: 'start', ...options });

    const [left, top] = this._offset();
    if (left || top) {
      this._scrollTarget().scrollBy({ left: -left, top: -top });
    }
  }

  setHistoryScrollRestoration(scrollRestoration: 'auto' | 'manual'): void {
    this._window.history.scrollRestoration = scrollRestoration;
  }

  private _scrollTarget(): HTMLElement | Window {
    return this._container.element ?? this._window;
  }
}
